import React, { useEffect } from "react";
import DwellingStyles from "./DwellingStyles";
import { Box, Card, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { GetPolicyRisks } from "../../../../store/user/policyDetails/policyDetailsActions";
import { IDwellingRisk } from "../../../../models/user/policyDetails/PolicyDetails";
import CustomIcon from "../../../../common/CustomIcon/CustomIcon";
import { capitalizeFirstLetter } from "../../../../utils/Utility";
import { useTranslation } from "react-i18next";
import CustomSkeleton from "../../../../common/CustomSkeleton/CustomSkeleton";

const Dwelling: React.FC = () => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const { policyId, carrier, system, lobCode } = useParams();

  const { calledReducerType, isLoading } = useAppSelector(
    (state) => state.common
  );

  const { policyDwellingRisks } = useAppSelector(
    (state) => state.policyDetails
  );
  useEffect(() => {
    if (policyId && carrier && system && lobCode) {
      dispatch(GetPolicyRisks({ policyId, carrier, system, lobCode }));
    }
  }, [policyId, dispatch, carrier, system, lobCode]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        padding: "20px",
      }}
      id="dwelling_container"
      data-test-id="dwelling_container"
      aria-label="Dwelling information container"
    >
      {isLoading && calledReducerType === "policyDetails/GetPolicyRisks" ? (
        <CustomSkeleton direction="column" height="120px" />
      ) : (
        policyDwellingRisks.map((risk: IDwellingRisk, index: number) => (
          <Card 
            sx={DwellingStyles.dwellingCard} 
            key={risk.ownerOccupied}
            id={`dwelling_card_${index}`}
            data-test-id={`dwelling_card_${index}`}
            aria-label={`Dwelling information card ${index + 1}`}
          >
            <Box 
              sx={DwellingStyles.containerTop}
              id={`dwelling_top_container_${index}`}
              data-test-id={`dwelling_top_container_${index}`}
              aria-label="Dwelling top information section"
            >
              <Box
                sx={{
                  display: "flex",
                  gap: "20px",
                  alignItems: "center",
                  overflow: "hidden",
                }}
                id={`dwelling_address_container_${index}`}
                data-test-id={`dwelling_address_container_${index}`}
                aria-label="Dwelling address information"
              >
                <CustomIcon iconName="Home" />
                <Box
                  id={`dwelling_address_details_${index}`}
                  data-test-id={`dwelling_address_details_${index}`}
                  aria-label="Dwelling address details"
                >
                  <Typography 
                    sx={DwellingStyles.claimsCardItemHeading}
                    id={`dwelling_address_${index}`}
                    data-test-id={`dwelling_address_${index}`}
                    aria-label={`Dwelling address: ${risk.address ? `${risk.address.addr1} ${risk.address.city} ${risk.address.stateProvCd} ${risk.address.regionISOCd} ${risk.address.postalCode}` : '-'}`}
                  >
                    {risk.address
                      ? `${risk.address.addr1} ${risk.address.city} ${risk.address.stateProvCd} ${risk.address.regionISOCd} ${risk.address.postalCode}`
                      : "-"}
                  </Typography>
                  <Typography 
                    sx={DwellingStyles.claimsCartItemContent}
                    id={`dwelling_address_label_${index}`}
                    data-test-id={`dwelling_address_label_${index}`}
                    aria-label="Address of the insured property"
                  >
                    {t("Address of the insured property")}
                  </Typography>
                </Box>
              </Box>

              <Box 
                sx={DwellingStyles.filedContainer}
                id={`dwelling_details_container_${index}`}
                data-test-id={`dwelling_details_container_${index}`}
                aria-label="Dwelling details section"
              >
                <Box
                  id={`dwelling_year_built_${index}`}
                  data-test-id={`dwelling_year_built_${index}`}
                  aria-label="Year built information"
                >
                  <Typography 
                    sx={DwellingStyles.claimsCardItemHeading}
                    id={`dwelling_year_built_value_${index}`}
                    data-test-id={`dwelling_year_built_value_${index}`}
                    aria-label={`Year built: ${risk.yearBuilt || '-'}`}
                  >
                    {risk.yearBuilt ? risk.yearBuilt : "-"}
                  </Typography>
                  <Typography 
                    sx={DwellingStyles.claimsCartItemContent}
                    id={`dwelling_year_built_label_${index}`}
                    data-test-id={`dwelling_year_built_label_${index}`}
                    aria-label="Year Built"
                  >
                    {t("Year Built")}
                  </Typography>
                </Box>
                <Box
                  id={`dwelling_year_purchased_${index}`}
                  data-test-id={`dwelling_year_purchased_${index}`}
                  aria-label="Year purchased information"
                >
                  <Typography 
                    sx={DwellingStyles.claimsCardItemHeading}
                    id={`dwelling_year_purchased_value_${index}`}
                    data-test-id={`dwelling_year_purchased_value_${index}`}
                    aria-label={`Year purchased: ${risk.yearPurchased || '-'}`}
                  >
                    {risk.yearPurchased ? risk.yearPurchased : "-"}
                  </Typography>
                  <Typography 
                    sx={DwellingStyles.claimsCartItemContent}
                    id={`dwelling_year_purchased_label_${index}`}
                    data-test-id={`dwelling_year_purchased_label_${index}`}
                    aria-label="Year Purchased"
                  >
                    {t("Year Purchased")}
                  </Typography>
                </Box>
                <Box
                  id={`dwelling_square_footage_${index}`}
                  data-test-id={`dwelling_square_footage_${index}`}
                  aria-label="Square footage information"
                >
                  <Typography 
                    sx={DwellingStyles.claimsCardItemHeading}
                    id={`dwelling_square_footage_value_${index}`}
                    data-test-id={`dwelling_square_footage_value_${index}`}
                    aria-label={`Square footage: ${risk.squareFootage || '-'}`}
                  >
                    {risk.squareFootage ? risk.squareFootage : "-"}
                  </Typography>
                  <Typography 
                    sx={DwellingStyles.claimsCartItemContent}
                    id={`dwelling_square_footage_label_${index}`}
                    data-test-id={`dwelling_square_footage_label_${index}`}
                    aria-label="Square Footage"
                  >
                    {t("Square Footage")}
                  </Typography>
                </Box>
                <Box
                  id={`dwelling_protection_class_${index}`}
                  data-test-id={`dwelling_protection_class_${index}`}
                  aria-label="Protection class rating information"
                >
                  <Typography 
                    sx={DwellingStyles.claimsCardItemHeading}
                    id={`dwelling_protection_class_value_${index}`}
                    data-test-id={`dwelling_protection_class_value_${index}`}
                    aria-label={`Protection class rating: ${risk.protectionClassRating || '-'}`}
                  >
                    {risk.protectionClassRating
                      ? risk.protectionClassRating
                      : "-"}
                  </Typography>
                  <Typography 
                    sx={DwellingStyles.claimsCartItemContent}
                    id={`dwelling_protection_class_label_${index}`}
                    data-test-id={`dwelling_protection_class_label_${index}`}
                    aria-label="Protection Class Rating"
                  >
                    {t("Protection Class Rating")}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box 
              sx={DwellingStyles.containerTwo}
              id={`dwelling_fire_details_${index}`}
              data-test-id={`dwelling_fire_details_${index}`}
              aria-label="Fire department and distance information"
            >
              <Box
                id={`dwelling_fire_department_${index}`}
                data-test-id={`dwelling_fire_department_${index}`}
                aria-label="Fire department details"
              >
                <Typography 
                  sx={DwellingStyles.claimsCardItemHeading}
                  id={`dwelling_fire_department_value_${index}`}
                  data-test-id={`dwelling_fire_department_value_${index}`}
                  aria-label={`Fire department details: ${risk.fireDepartmentDetails ? capitalizeFirstLetter(risk.fireDepartmentDetails) : '-'}`}
                >
                  {risk.fireDepartmentDetails
                    ? capitalizeFirstLetter(risk.fireDepartmentDetails)
                    : "-"}
                </Typography>
                <Typography 
                  sx={DwellingStyles.claimsCartItemContent}
                  id={`dwelling_fire_department_label_${index}`}
                  data-test-id={`dwelling_fire_department_label_${index}`}
                  aria-label="Fire Department Details"
                >
                  {t("Fire Department Details")}
                </Typography>
              </Box>
              <Box
                id={`dwelling_fire_station_distance_${index}`}
                data-test-id={`dwelling_fire_station_distance_${index}`}
                aria-label="Distance to fire station information"
              >
                <Typography 
                  sx={DwellingStyles.claimsCardItemHeading}
                  id={`dwelling_fire_station_distance_value_${index}`}
                  data-test-id={`dwelling_fire_station_distance_value_${index}`}
                  aria-label={`Distance to fire station: ${risk.distanceToFireStation === "1" ? t("Less than 5 miles") : risk.distanceToFireStation === "2" ? t("5-7 miles") : risk.distanceToFireStation === "3" ? t("More than 7 miles") : '-'}`}
                >
                  {risk.distanceToFireStation === "1"
                    ? t("Less than 5 miles")
                    : risk.distanceToFireStation === "2"
                    ? t("5-7 miles")
                    : risk.distanceToFireStation === "3"
                    ? t("More than 7 miles")
                    : "-"}
                </Typography>
                <Typography 
                  sx={DwellingStyles.claimsCartItemContent}
                  id={`dwelling_fire_station_distance_label_${index}`}
                  data-test-id={`dwelling_fire_station_distance_label_${index}`}
                  aria-label="Distance to Fire Station"
                >
                  {t("Distance to Fire Station")}
                </Typography>
              </Box>
              <Box
                id={`dwelling_owner_occupied_${index}`}
                data-test-id={`dwelling_owner_occupied_${index}`}
                aria-label="Owner occupied information"
              >
                <Typography 
                  sx={DwellingStyles.claimsCardItemHeading}
                  id={`dwelling_owner_occupied_value_${index}`}
                  data-test-id={`dwelling_owner_occupied_value_${index}`}
                  aria-label={`Owner occupied: ${risk.ownerOccupied ? capitalizeFirstLetter(risk.ownerOccupied) : '-'}`}
                >
                  {risk.ownerOccupied
                    ? capitalizeFirstLetter(risk.ownerOccupied)
                    : "-"}
                </Typography>
                <Typography 
                  sx={DwellingStyles.claimsCartItemContent}
                  id={`dwelling_owner_occupied_label_${index}`}
                  data-test-id={`dwelling_owner_occupied_label_${index}`}
                  aria-label="Owner Occupied"
                >
                  {t("Owner Occupied")}
                </Typography>
              </Box>
              <Box
                id={`dwelling_construction_type_${index}`}
                data-test-id={`dwelling_construction_type_${index}`}
                aria-label="Construction type information"
              >
                <Typography 
                  sx={DwellingStyles.claimsCardItemHeading}
                  id={`dwelling_construction_type_value_${index}`}
                  data-test-id={`dwelling_construction_type_value_${index}`}
                  aria-label={`Construction type: ${risk.constructionType || '-'}`}
                >
                  {risk.constructionType ? risk.constructionType : "-"}
                </Typography>
                <Typography 
                  sx={DwellingStyles.claimsCartItemContent}
                  id={`dwelling_construction_type_label_${index}`}
                  data-test-id={`dwelling_construction_type_label_${index}`}
                  aria-label="Construction Type"
                >
                  {t("Construction Type")}
                </Typography>
              </Box>
            </Box>

            <Box 
              sx={DwellingStyles.containerTwo}
              id={`dwelling_heating_details_${index}`}
              data-test-id={`dwelling_heating_details_${index}`}
              aria-label="Heating system information"
            >
              <Box
                id={`dwelling_heating_system_${index}`}
                data-test-id={`dwelling_heating_system_${index}`}
                aria-label="Type of heating system information"
              >
                <Typography 
                  sx={DwellingStyles.claimsCardItemHeading}
                  id={`dwelling_heating_system_value_${index}`}
                  data-test-id={`dwelling_heating_system_value_${index}`}
                  aria-label={`Type of heating system: ${risk.typeOfHeatingSystem || '-'}`}
                >
                  {risk.typeOfHeatingSystem ? risk.typeOfHeatingSystem : "-"}
                </Typography>
                <Typography 
                  sx={DwellingStyles.claimsCartItemContent}
                  id={`dwelling_heating_system_label_${index}`}
                  data-test-id={`dwelling_heating_system_label_${index}`}
                  aria-label="Type Of Heating System"
                >
                  {t("Type Of Heating System")}
                </Typography>
              </Box>
              <Box
                id={`dwelling_heating_year_${index}`}
                data-test-id={`dwelling_heating_year_${index}`}
                aria-label="Year heating updated information"
              >
                <Typography 
                  sx={DwellingStyles.claimsCardItemHeading}
                  id={`dwelling_heating_year_value_${index}`}
                  data-test-id={`dwelling_heating_year_value_${index}`}
                  aria-label={`Year heating updated: ${risk.yearHeatingUpdated || '-'}`}
                >
                  {risk.yearHeatingUpdated ? risk.yearHeatingUpdated : "-"}
                </Typography>
                <Typography 
                  sx={DwellingStyles.claimsCartItemContent}
                  id={`dwelling_heating_year_label_${index}`}
                  data-test-id={`dwelling_heating_year_label_${index}`}
                  aria-label="Year Heating Updated"
                >
                  {t("Year Heating Updated")}
                </Typography>
              </Box>
              <Box
                id={`dwelling_pets_${index}`}
                data-test-id={`dwelling_pets_${index}`}
                aria-label="Pets information"
              >
                <Typography 
                  sx={DwellingStyles.claimsCardItemHeading}
                  id={`dwelling_pets_value_${index}`}
                  data-test-id={`dwelling_pets_value_${index}`}
                  aria-label={`Pets: ${risk.numberOfPets ? capitalizeFirstLetter(risk.numberOfPets) : '-'}`}
                >
                  {risk.numberOfPets
                    ? capitalizeFirstLetter(risk.numberOfPets)
                    : "-"}
                </Typography>
                <Typography 
                  sx={DwellingStyles.claimsCartItemContent}
                  id={`dwelling_pets_label_${index}`}
                  data-test-id={`dwelling_pets_label_${index}`}
                  aria-label="Pets"
                >
                  {t("Pets")}
                </Typography>
              </Box>
              <Box
                id={`dwelling_construction_status_${index}`}
                data-test-id={`dwelling_construction_status_${index}`}
                aria-label="Dwelling construction status information"
              >
                <Typography
                  sx={{
                    ...DwellingStyles.claimsCardItemHeading,
                    whiteSpace: "wrap",
                  }}
                  id={`dwelling_construction_status_value_${index}`}
                  data-test-id={`dwelling_construction_status_value_${index}`}
                  aria-label={`Dwelling in course of construction: ${risk.dwellingInCourseOfConstruction || '-'}`}
                >
                  {risk.dwellingInCourseOfConstruction
                    ? risk.dwellingInCourseOfConstruction
                    : "-"}
                </Typography>
                <Typography
                  sx={{
                    ...DwellingStyles.claimsCartItemContent,
                    "@media (min-width: 1025px) and (max-width: 1366px)": {
                      textOverflow: "ellipsis",
                      whiteSpace: "wrap",
                    },
                  }}
                  id={`dwelling_construction_status_label_${index}`}
                  data-test-id={`dwelling_construction_status_label_${index}`}
                  aria-label="Dwelling In Course of Construction"
                >
                  {t("Dwelling In Course of Construction")}
                </Typography>
              </Box>
            </Box>
            <Box 
              sx={DwellingStyles.containerTwo}
              id={`dwelling_structure_details_${index}`}
              data-test-id={`dwelling_structure_details_${index}`}
              aria-label="Dwelling structure information"
            >
              <Box
                id={`dwelling_type_${index}`}
                data-test-id={`dwelling_type_${index}`}
                aria-label="Dwelling type information"
              >
                <Typography 
                  sx={DwellingStyles.claimsCardItemHeading}
                  id={`dwelling_type_value_${index}`}
                  data-test-id={`dwelling_type_value_${index}`}
                  aria-label={`Dwelling type: ${risk.dwellingType || '-'}`}
                >
                  {risk.dwellingType ? risk.dwellingType : "-"}
                </Typography>
                <Typography 
                  sx={DwellingStyles.claimsCartItemContent}
                  id={`dwelling_type_label_${index}`}
                  data-test-id={`dwelling_type_label_${index}`}
                  aria-label="Dwelling Type"
                >
                  {t("Dwelling Type")}
                </Typography>
              </Box>
              <Box
                id={`dwelling_families_${index}`}
                data-test-id={`dwelling_families_${index}`}
                aria-label="Number of families information"
              >
                <Typography 
                  sx={DwellingStyles.claimsCardItemHeading}
                  id={`dwelling_families_value_${index}`}
                  data-test-id={`dwelling_families_value_${index}`}
                  aria-label={`Number of families: ${risk.numberOfFamilies || '-'}`}
                >
                  {risk.numberOfFamilies ? risk.numberOfFamilies : "-"}
                </Typography>
                <Typography 
                  sx={DwellingStyles.claimsCartItemContent}
                  id={`dwelling_families_label_${index}`}
                  data-test-id={`dwelling_families_label_${index}`}
                  aria-label="Number of Families"
                >
                  {t("Number of Families")}
                </Typography>
              </Box>
              <Box
                id={`dwelling_roof_type_${index}`}
                data-test-id={`dwelling_roof_type_${index}`}
                aria-label="Roof type information"
              >
                <Typography 
                  sx={DwellingStyles.claimsCardItemHeading}
                  id={`dwelling_roof_type_value_${index}`}
                  data-test-id={`dwelling_roof_type_value_${index}`}
                  aria-label={`Roof type: ${risk.roofType || '-'}`}
                >
                  {risk.roofType ? risk.roofType : "-"}
                </Typography>
                <Typography 
                  sx={DwellingStyles.claimsCartItemContent}
                  id={`dwelling_roof_type_label_${index}`}
                  data-test-id={`dwelling_roof_type_label_${index}`}
                  aria-label="Roof Type"
                >
                  {t("Roof Type")}
                </Typography>
              </Box>
              <Box
                id={`dwelling_roof_year_${index}`}
                data-test-id={`dwelling_roof_year_${index}`}
                aria-label="Year roof updated information"
              >
                <Typography 
                  sx={DwellingStyles.claimsCardItemHeading}
                  id={`dwelling_roof_year_value_${index}`}
                  data-test-id={`dwelling_roof_year_value_${index}`}
                  aria-label={`Year roof updated: ${risk.yearRoofUpdated || '-'}`}
                >
                  {risk.yearRoofUpdated ? risk.yearRoofUpdated : "-"}
                </Typography>
                <Typography 
                  sx={DwellingStyles.claimsCartItemContent}
                  id={`dwelling_roof_year_label_${index}`}
                  data-test-id={`dwelling_roof_year_label_${index}`}
                  aria-label="Year Roof Updated"
                >
                  {t("Year Roof Updated")}
                </Typography>
              </Box>
            </Box>
          </Card>
        ))
      )}
    </Box>
  );
};

export default Dwelling;
